package com.rylcart.cp.ui.state

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.rylcart.cp.ui.api.AuthStorage
import com.rylcart.cp.ui.api.CpApi
import kotlinx.browser.window

/**
 * Tiny global state for the admin SPA: router + auth token + transient toast.
 * Compose's CompositionLocal gives us free DI without an extra framework.
 */
class AppState {
    var token: String? by mutableStateOf(AuthStorage.get())
        private set
    var route: Route by mutableStateOf(parseHash(window.location.hash))
    var toast: ToastMessage? by mutableStateOf(null)

    val api: CpApi = CpApi(baseUrl = window.location.origin, authProvider = { token })

    fun setToken(t: String?) {
        token = t
        if (t == null) AuthStorage.clear() else AuthStorage.set(t)
    }

    fun navigate(r: Route) {
        route = r
        window.location.hash = r.hash
    }

    fun showError(msg: String) { toast = ToastMessage(msg, isError = true) }
    fun showOk(msg: String)    { toast = ToastMessage(msg, isError = false) }
}

data class ToastMessage(val text: String, val isError: Boolean)

sealed class Route(val hash: String) {
    data object Tenants : Route("#/tenants")
    data object NewTenant : Route("#/tenants/new")
    data class TenantDetail(val id: String) : Route("#/tenants/$id")

    companion object
}

fun parseHash(hash: String): Route {
    val h = hash.removePrefix("#")
    return when {
        h.startsWith("/tenants/new") -> Route.NewTenant
        h.startsWith("/tenants/")    -> Route.TenantDetail(h.removePrefix("/tenants/"))
        else                         -> Route.Tenants
    }
}

val LocalApp = compositionLocalOf<AppState> { error("AppState not provided") }

@Composable
fun AppStateProvider(content: @Composable () -> Unit) {
    val state = remember { AppState() }
    // Hash change listener — back/forward buttons should re-render.
    androidx.compose.runtime.DisposableEffect(Unit) {
        val handler: (org.w3c.dom.events.Event) -> Unit = { state.route = parseHash(window.location.hash) }
        window.addEventListener("hashchange", handler)
        onDispose { window.removeEventListener("hashchange", handler) }
    }
    CompositionLocalProvider(LocalApp provides state) { content() }
}
